// src/components/AlmChart.js
import React from "react";
import { Typography, Box } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer,
} from "recharts";

export default function AlmChart({ data, shockSelection = 'No Shock', scaleEnabled = false }) {
  // Helper function to get the correct data field based on shock selection
  function getDataField() {
    const baseField = 'data';
    if (shockSelection === 'Up 100 shock') {
      return `${baseField}_up`;
    } else if (shockSelection === 'Down 100 shock') {
      return `${baseField}_down`;
    }
    return baseField; // No Shock - uses 'data'
  }

  // Get the appropriate data based on shock selection
  const selectedField = getDataField();
  const chartData = data && data[selectedField];
  
  // Apply scaling if enabled
  const processedChartData = React.useMemo(() => {
    if (!Array.isArray(chartData)) return chartData;
    
    if (!scaleEnabled) return chartData;
    
    return chartData.map(point => {
      const scalar = point.scalar || 1;
      return {
        ...point,
        asset_cf: (point.asset_cf || 0) * scalar,
        liability_cf: (point.liability_cf || 0) * scalar,
        net_cf: (point.net_cf || 0) * scalar,
        cumulative_net_cf: (point.cumulative_net_cf || 0) * scalar
      };
    });
  }, [chartData, scaleEnabled]);
  
  // Check if data is valid (array) and not an error object
  const isValidData = Array.isArray(processedChartData) && processedChartData.length > 0;
  const hasError = processedChartData && !Array.isArray(processedChartData) && processedChartData.error;
  
  const defaultStartIndex = 0;
  const defaultEndIndex = isValidData ? Math.min(10, chartData.length - 1) : 0;

  // Calculate Y-axis configuration with fixed intervals
  const yAxisConfig = React.useMemo(() => {
    if (!data) return { domain: undefined, ticks: [] };

    const allValues = [];
    ['data', 'data_up', 'data_down'].forEach(field => {
      if (Array.isArray(data[field])) {
        data[field].forEach(point => {
          const scalar = scaleEnabled ? (point.scalar || 1) : 1;
          allValues.push(
            Math.abs((point.asset_cf || 0) * scalar),
            Math.abs((point.liability_cf || 0) * scalar),
            Math.abs((point.net_cf || 0) * scalar),
            Math.abs((point.cumulative_net_cf || 0) * scalar)
          );
        });
      }
    });

    if (allValues.length === 0) return { domain: undefined, ticks: [] };

    // Find maximum absolute value and round up to nearest 5000
    const maxAbs = Math.max(...allValues);
    const roundedMax = Math.ceil(maxAbs / 5000) * 5000;
    // Create ticks with 2 ticks on either side of 0
    const ticks = [-roundedMax, -roundedMax/2, 0, roundedMax/2, roundedMax];
    
    return {
      domain: [-roundedMax, roundedMax],
      ticks
    };
  }, [data, scaleEnabled]);

  return (
    <Box sx={{ mb: 3 }}>
      {hasError ? (
        <Typography variant="body1" color="error">
          Error loading {shockSelection} data: {chartData.error}
        </Typography>
              ) : !isValidData ? (
          <Typography variant="body1" color="text.secondary">
            No ALM data available for {shockSelection}.
          </Typography>
        ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart 
            data={processedChartData}
            margin={{ left: 15 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Date" tick={{ fontSize: 12 }} />
            <YAxis 
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              tickFormatter={val => `$${val.toLocaleString()}`}
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              formatter={(value, name) => [
                typeof value === 'number' ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value,
                name
              ]}
            />
            <Legend />
            <Bar dataKey="asset_cf" name="Asset CF" fill="#8884d8" />
            <Bar dataKey="liability_cf" name="Liability CF" fill="#82ca9d" />
            <Bar dataKey="net_cf" name="Net CF" fill="#ff7300" />
            <Line
              type="monotone"
              dataKey="cumulative_net_cf"
              name="Cumulative Net CF"
              stroke="#000"
              dot={false}
            />
            <Brush
              dataKey="Date"
              startIndex={defaultStartIndex}
              endIndex={defaultEndIndex}
              height={30}
              stroke="#8884d8"
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}
